import type {
  ProvenanceDocumentKind,
  ProvenanceEntryKind,
  ProvenanceEvidenceClaim,
} from "@ondo/shared";
import { useId } from "react";

import { useModalDialog } from "../lib/useModalDialog.ts";
import { HistoryFields } from "./HistoryFields.tsx";
import { HistoryKindChips } from "./HistoryKindChips.tsx";
import { ProvenanceDocuments } from "./ProvenanceDocuments.tsx";
import { ProvenanceVerification } from "./ProvenanceVerification.tsx";
import {
  EVIDENCE_GRADES,
  HISTORY_COPY,
  PROVENANCE_COPY,
  PROVENANCE_LABELS,
} from "./provenance.constants.ts";
import type {
  AddHistoryView,
  DraftEntry,
  VerificationIntent,
} from "./provenance.model.ts";

import "../lib/dialog.css";

/**
 * Adding a piece of artwork history.
 *
 * A native `<dialog>` opened with `showModal()`, the same choice
 * `WorkPickerDialog` makes and for the same list of things it does that would
 * otherwise have to be written and got right: modal semantics, Escape, a real
 * focus trap, the rest of the document made INERT rather than merely covered,
 * focus returned to whatever opened it, and the top layer — no portal, no
 * z-index scale to invent.
 *
 * CLOSING DOES NOT DISCARD. Escape, the backdrop and ✕ put the draft down; they
 * do not throw it away, and pressing "+ Add history" again picks it back up. The
 * argument is `WorkPickerDialog`'s: a stray Escape must not cost somebody
 * twenty minutes of typing. Cancel is the one control that discards, because
 * that is the only thing Cancel can mean.
 *
 * MOUNTED EVEN WHEN SHUT, deliberately. Closing by unmounting would skip
 * `close()`, and it is `close()` that returns focus to the button that opened
 * this.
 */
export function AddHistoryDialog({
  view,
  onChooseKind,
  onSetField,
  onAddDocuments,
  onSetDocumentKind,
  onOpenDocument,
  onRemoveDocument,
  onAttachOndoId,
  onDetachOndoId,
  onAct,
  onUnact,
  onSubmit,
  onCancel,
  onClose,
}: {
  view: AddHistoryView;
  onChooseKind: (kind: ProvenanceEntryKind) => void;
  onSetField: <K extends keyof DraftEntry>(key: K, value: DraftEntry[K]) => void;
  onAddDocuments: (files: File[]) => void;
  onSetDocumentKind: (documentId: string, kind: ProvenanceDocumentKind) => void;
  onOpenDocument: (documentId: string) => void;
  onRemoveDocument: (documentId: string) => void;
  onAttachOndoId: (raw: string) => string | null;
  onDetachOndoId: () => void;
  onAct: (intent: VerificationIntent) => string | null;
  onUnact: (actId: string) => void;
  onSubmit: () => void;
  /** Discards the draft. The only control that does. */
  onCancel: () => void;
  /** Puts it down. Escape, the backdrop, and ✕. */
  onClose: () => void;
}) {
  const dialog = useModalDialog(view.open);
  const titleId = useId();
  const base = useId();

  return (
    <dialog
      ref={dialog}
      className="history"
      aria-labelledby={titleId}
      // Escape and the backdrop both close the dialog natively; this is what
      // tells React it happened. Without it the two disagree and the next
      // render calls showModal() on a dialog that is already open.
      onClose={onClose}
    >
      <div className="history__head ondo-dialog__head">
        <div>
          <h2 id={titleId} className="history__title">
            {HISTORY_COPY.dialogTitle}
          </h2>
          {view.open && view.subject !== null && (
            <p className="history__subject">{view.subject}</p>
          )}
        </div>
        <button
          type="button"
          className="history__close ondo-dialog__close"
          aria-label={HISTORY_COPY.close}
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {view.open && (
        <div className="history__body">
          <section className="history__section">
            <h3 className="history__legend">{HISTORY_COPY.kindLegend}</h3>
            <HistoryKindChips chips={view.chips} onChoose={onChooseKind} />
            <p className="provenance__help">{HISTORY_COPY.kindHelp}</p>
          </section>

          {view.form === null ? (
            <p className="history__prompt">{HISTORY_COPY.chooseKind}</p>
          ) : (
            <>
              <section className="history__section">
                <HistoryFields
                  base={base}
                  values={view.form.values}
                  ondo={view.form.ondo}
                  onSetField={onSetField}
                  onAttachOndoId={onAttachOndoId}
                  onDetachOndoId={onDetachOndoId}
                />

                <div className="provenance__field">
                  <label htmlFor={`${base}-grade`}>{PROVENANCE_LABELS.grade}</label>
                  <select
                    id={`${base}-grade`}
                    value={view.form.values.grade}
                    onChange={(event) =>
                      onSetField("grade", event.target.value as ProvenanceEvidenceClaim)
                    }
                  >
                    {EVIDENCE_GRADES.map((grade) => (
                      <option key={grade.key} value={grade.key}>
                        {grade.label}
                      </option>
                    ))}
                  </select>
                  <p className="provenance__help">{PROVENANCE_COPY.gradeHelp}</p>
                </div>

                <ProvenanceDocuments
                  rows={view.form.documents}
                  refusals={view.refusals}
                  editable
                  onAdd={onAddDocuments}
                  onSetKind={onSetDocumentKind}
                  onOpen={onOpenDocument}
                  onRemove={onRemoveDocument}
                  // Unreachable: a modal composes a claim that is not on the
                  // registry yet, so its documents are files with no row to
                  // hang a verdict off. `ProvenanceEntryForm` says the same.
                  onJudge={() => null}
                  onUndoVerdict={() => undefined}
                />
              </section>

              <section className="history__section history__corroborate">
                <h3 className="history__legend">{HISTORY_COPY.corroborateTitle}</h3>
                <p className="provenance__help">{HISTORY_COPY.corroborateHelp}</p>

                {/* Where the design puts three avatars of suggested people.
                    There is no name search on this platform and there is not
                    going to be one here — see HISTORY_COPY.corroborateNoSearch,
                    which is the sentence that replaces them. */}
                <p className="history__nosearch">{HISTORY_COPY.corroborateNoSearch}</p>

                {view.acts !== null && (
                  <ProvenanceVerification
                    model={view.acts}
                    onAct={onAct}
                    onUnact={onUnact}
                  />
                )}
              </section>
            </>
          )}

          {view.problem !== null && (
            <p className="provenance__problem" role="alert">
              {view.problem}
            </p>
          )}

          <p className="provenance__callout">{view.finalNote}</p>

          <div className="history__actions">
            <button type="button" className="history__cancel" onClick={onCancel}>
              {HISTORY_COPY.cancel}
            </button>
            <button
              type="button"
              className="history__submit"
              disabled={view.form === null || view.busy}
              onClick={onSubmit}
            >
              {view.busy ? HISTORY_COPY.submitting : HISTORY_COPY.submit}
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}
